/** @format */

import React from "react";
import { Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useLocale } from "../i18n/hooks";
import i18n from "../i18n/init";
import stringsCy from "./locales/strings.cy.json";
import ErrorIllustration401 from "../../assets/images/errorPages/401-unauthorised-illustration.svg";
import ErrorIllustration403 from "../../assets/images/errorPages/403-forbidden-illustration.svg";
import ErrorIllustration404 from "../../assets/images/errorPages/404-not-found-illustration.svg";
import ErrorIllustration500 from "../../assets/images/errorPages/500-internal-server-error-illustration.svg";
import { ReactComponent as WJECLogo } from "./../../assets/images/wjecLogo.svg";
import { ReactComponent as CBACLogo } from "./../../assets/images/cbacLogo.svg";
import "./ErrorPage.scss";

type Props = {
  statusCode?: number;
  statusMessage?: string;
  hiddenMessage?: string;
  actions: ("report" | "newResource" | "reload")[];
  className?: string;
};

/** TYPICAL USAGE WITH RTK QUERY ERRORS, where `error` is the error object returned by RTK
 * ``` TSX
  <ErrorPage
    statusCode={
      "status" in error &&
      typeof error.status === "number"
        ? error.status
        : undefined
    }
    hiddenMessage={
      "data" in error && typeof error.data === "string"
        ? error.data
        : undefined
    }
    actions={["report", "newResource"]}
  />
* ```
*/

/**
 * This component uses either a relevant status code or message to display a formatted error to the user
 * @param statusCode default messages for 400, 401, 403, 404, and 500. See https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
 *  NOTE: these messages do not translate well and should only be used without `statusMessage` in place for an unpredicted error code
 *
 * @param statusMessage overrides default message from status code
 *
 * @param hiddenMessage the message to be reported with the report action
 *
 * @param actions "report": a mailto button to report the error.
 *  "newResource": a link to the RF site.
 *  "reload": a reload page button (also shows when the status and message are undefined)
 *  TODO: add a login action when sure what the login url will be
 *
 * @param className overrides the default className
 */
const ErrorPage: React.FC<Props> = ({
  statusCode,
  statusMessage,
  hiddenMessage,
  actions,
  className,
}) => {
  const { t: _ } = useTranslation();
  const locale = useLocale();
  i18n.addResources("cy", "translation", stringsCy);
  const navigate = useNavigate();

  const errorIllustrationVariant = () => {
    switch (statusCode) {
      case 401:
        return ErrorIllustration401;
      case 403:
        return ErrorIllustration403;
      case 404:
        return ErrorIllustration404;
      case 500:
        return ErrorIllustration500;
    }
  };

  return (
    <div className="container">
      <div className="error-container p-4 p-md-5" data-testid="error">
        <div className="error-message">
          {locale === "en" && <WJECLogo className="logo" />}
          {locale === "cy" && <CBACLogo className="logo" />}
          {statusMessage ? (
            <h4 data-testid="statusMsg">{statusMessage}</h4>
          ) : statusCode === 400 ? (
            <h4 data-testid="defaultMsg">{_("Bad request")}</h4>
          ) : statusCode === 401 ? (
            <>
              <h4>{_("401 Unauthorised")}</h4>
              <p>{_("You're unauthorised to access this page.")}</p>
            </>
          ) : statusCode === 403 ? (
            <>
              <h4>{_("403 Forbidden")}</h4>
              <p>{_("Oops! You don't have permission to view this page.")}</p>
            </>
          ) : statusCode === 404 ? (
            <>
              <h4>{_("404 Page Not Found")}</h4>
              <p>
                {_("Oops! It seems we're having difficulty finding this page.")}
              </p>
            </>
          ) : (
            statusCode === 500 && (
              <>
                <h4>{_("500 Internal Server Error")}</h4>
                <p>{_("Oops! It seems something has gone wrong.")}</p>
              </>
            )
          )}

          {!statusCode && statusMessage === undefined && (
            <h5>
              {_(
                "Unknown error, please attempt to reload the page before reporting it.",
              )}
            </h5>
          )}
          <p>
            {_("Click to")}{" "}
            <button onClick={() => navigate(-1)}>{_("go back.")}</button>
          </p>
        </div>
        <div className="image-container">
          <Image
            src={errorIllustrationVariant()}
            alt={`${statusCode} illustration`}
            className="error-illustration"
          />
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
